import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useColorMode, Box, Spinner } from '@chakra-ui/react';
import { FaArrowLeft, FaPrint, FaLinkedinIn, FaFacebookF, FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import JournalRender from './JournalRender';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PRINT_CSS = `
  /* Force l'impression des couleurs de fond */
  *, *::before, *::after {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  html, body { margin: 0; padding: 0; background: white; font-family: "Helvetica Neue", Arial, sans-serif; }

  .journal-body p            { margin-bottom: 0.8em; }
  .journal-body ul,
  .journal-body ol           { padding-left: 1.2em; margin-bottom: 0.8em; }
  .journal-body strong       { font-weight: 700; }
  .journal-body em           { font-style: italic; }
  .journal-body blockquote   { border-left: 3px solid #ccc; padding: 4px 12px; margin: 0.8em 0; font-style: italic; }
  .journal-body p            { orphans: 3; widows: 3; }

  .ql-editor { padding: 0; }
  .ql-editor p { margin-bottom: 0.8em; }

  .journal-image img, .journal-cover-image img { max-width: 100%; height: auto; display: block; }
  .journal-image figcaption  { margin-top: 6px; font-size: 0.7rem; color: #888; font-style: italic; text-align: center; }
  .journal-cover-image       { page-break-inside: avoid; break-inside: avoid; }
  .journal-article,
  .journal-section,
  .journal-quote,
  .journal-highlight,
  .journal-callout,
  .journal-banner,
  .journal-stats,
  .journal-contact,
  .journal-pull-quote,
  .journal-team-member,
  .journal-feature-photo,
  .journal-toc-cover,
  .journal-section-divider,
  .journal-newsletter        { break-inside: avoid; page-break-inside: avoid; }

  /* Grille deux colonnes en impression */
  .journal-two-columns       { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }

  /* Recto → Verso */
  .journal-page-1            { page-break-after: always; break-after: page; }
  .journal-page-2            { page-break-before: always; break-before: page; }

  /* L'astuce aspect-ratio + flex (utilisée à l'écran) déstabilise la pagination
     d'impression de Chrome (double réservation d'espace). On la neutralise ici
     et on revient à un simple min-height : le contenu s'écoule normalement et
     l'espace restant éventuel se retrouve en fin de page, après le pied de page. */
  .journal-page         { aspect-ratio: auto !important; display: block !important; min-height: 273mm !important; }
  .journal-page-content { flex: none !important; display: block !important; }

  /* Masquer éléments web uniquement */
  .no-print                  { display: none !important; }
  .print-guide               { display: none !important; }

  @page {
    size: A4 portrait;
    /* marges réduites pour compenser les en-têtes navigateur */
    margin: 12mm 16mm 12mm 16mm;
  }
`;

export default function JournalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(true);
  const journalRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/journal/${id}`)
      .then(res => setJournal(res.data))
      .catch(() => navigate('/journal'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handlePrint = () => {
    const el = journalRef.current;
    if (!el) return;

    const win = window.open('', '_blank', 'width=860,height=720');
    if (!win) return;

    const accent = journal?.layoutData?.accentColor || '#274559';

    win.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${journal?.title || 'Journal HIS'}</title>
  <style>
    ${PRINT_CSS}

    /* ── Guide d'impression (visible uniquement à l'écran) ── */
    @media screen {
      .print-guide {
        display: flex !important;
        position: fixed; inset: 0;
        background: rgba(15,23,42,0.88);
        align-items: center; justify-content: center;
        z-index: 9999; font-family: sans-serif;
      }
      .print-guide-box {
        background: #fff; border-radius: 16px; padding: 36px 40px;
        max-width: 420px; width: 90%; text-align: center;
        box-shadow: 0 24px 64px rgba(0,0,0,0.4);
      }
      .pg-title { margin: 0 0 8px; font-size: 1.25rem; font-weight: 800; color: #111; }
      .pg-sub   { margin: 0 0 24px; font-size: 0.85rem; color: #64748b; line-height: 1.6; }
      .pg-steps { list-style: none; padding: 0; margin: 0 0 28px; text-align: left; }
      .pg-steps li {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 10px 14px; border-radius: 10px; margin-bottom: 8px;
        background: #f8fafc; font-size: 0.83rem; color: #334155; line-height: 1.5;
      }
      .pg-steps li span.icon { font-size: 1.1rem; flex-shrink: 0; margin-top: 1px; }
      .pg-btn {
        width: 100%; padding: 13px; border: none; border-radius: 10px;
        background: ${accent}; color: #fff;
        font-size: 1rem; font-weight: 800; cursor: pointer; letter-spacing: 0.02em;
      }
      .pg-btn:hover { opacity: 0.9; }
      .pg-note { margin-top: 12px; font-size: 0.73rem; color: #94a3b8; }
    }
    @media print {
      .print-guide { display: none !important; }
    }
  </style>
</head>
<body>

  <!-- Guide interactif avant impression -->
  <div class="print-guide" id="pg">
    <div class="print-guide-box">
      <p class="pg-title">🖨️ Avant d'imprimer</p>
      <p class="pg-sub">Pour que le journal soit fidèle à l'original, suivez ces étapes dans la boîte de dialogue.</p>
      <ul class="pg-steps">
        <li><span class="icon">✅</span><div>Cochez <strong>"Graphiques d'arrière-plan"</strong> — indispensable pour les fonds et couleurs</div></li>
        <li><span class="icon">☑️</span><div>Décochez <strong>"En-têtes et pieds de page"</strong> — supprime la date et l'URL</div></li>
        <li><span class="icon">📄</span><div>Choisissez <strong>"Aucune marge"</strong> ou <strong>"Marges minimales"</strong> pour maximiser l'espace</div></li>
        <li><span class="icon">📑</span><div>Sélectionnez <strong>"Toutes les pages"</strong></div></li>
      </ul>
      <button class="pg-btn" onclick="document.getElementById('pg').style.display='none'; setTimeout(function(){ window.print(); }, 150);">
        OK — Ouvrir l'impression
      </button>
      <p class="pg-note">Le journal s'imprimera recto (page 1) puis verso (page 2) sur votre imprimante.</p>
    </div>
  </div>

  ${el.innerHTML}
</body>
</html>`);
    win.document.close();
  };

  const bg = isDark ? '#1e293b' : '#c8cdd5';

  if (loading) return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg={isDark ? '#0a0f16' : '#f7faff'}>
      <Spinner size="xl" thickness="4px" color="blue.500" />
    </Box>
  );

  if (!journal) return null;

  const layout      = journal.layoutData || {};
  const accentColor = layout.accentColor || '#274559';
  const columnCount = layout.columnCount || 2;
  const blocks      = layout.blocks || [];

  return (
    <Box minH="100vh" bg={bg} pt="80px" pb="60px">
      <Helmet>
        <title>{journal.title} | Journal HIS</title>
        <meta name="description" content={journal.excerpt || journal.title} />
      </Helmet>

      {/* Barre navigation */}
      <div style={{ maxWidth: '780px', margin: '0 auto', padding: '0 16px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <motion.button
          initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/journal')}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <FaArrowLeft size={12} /> Tous les journaux
        </motion.button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Partage */}
          {[
            { icon: FaLinkedinIn, color: '#0077B5', label: 'LinkedIn', href: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}` },
            { icon: FaFacebookF,  color: '#1877F2', label: 'Facebook', href: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
            { icon: FaWhatsapp,   color: '#25D366', label: 'WhatsApp', href: () => `https://api.whatsapp.com/send?text=${encodeURIComponent(journal.title + ' — ' + window.location.href)}` },
          ].map(({ icon: Icon, color, label, href }) => (
            <a key={label} href={href()} target="_blank" rel="noopener noreferrer" title={`Partager sur ${label}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '8px', background: color, color: '#fff', fontSize: '14px', textDecoration: 'none' }}
            >
              <Icon size={14} />
            </a>
          ))}

          <button
            onClick={handlePrint}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, color: '#fff', background: accentColor, border: 'none', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          >
            <FaPrint size={12} /> Imprimer / PDF
          </button>
        </div>
      </div>

      {/* Journal */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        style={{ maxWidth: '780px', margin: '0 auto', padding: '0 16px', boxShadow: '0 4px 40px rgba(0,0,0,0.18)', borderRadius: '6px', overflow: 'hidden' }}
      >
        <div ref={journalRef}>
          <JournalRender
            title={journal.title}
            period={journal.period}
            excerpt={journal.excerpt}
            accentColor={accentColor}
            columnCount={columnCount}
            blocks={blocks}
            page1Settings={layout.page1Settings}
            page2Settings={layout.page2Settings}
            hashtag={layout.hashtag}
            issueLabel={layout.issueLabel}
            runningLabel={layout.runningLabel}
            publishedAt={journal.createdAt}
          />
        </div>
      </motion.div>
    </Box>
  );
}
